import { create } from 'zustand';
import { uuidV4 } from '../crypto/random.js';

export type NotificationType = 'info' | 'success' | 'warning' | 'error';

export interface NotificationAction {
  label: string;
  onClick: () => void;
}

export interface Notification {
  id: string;
  type: NotificationType;
  message: string;
  title?: string;
  action?: NotificationAction;
  durationMs?: number;
  createdAt: number;
}

export interface NotificationInput {
  type: NotificationType;
  message: string;
  title?: string;
  action?: NotificationAction;
  durationMs?: number;
}

export interface NotificationState {
  notifications: Notification[];
  push: (n: NotificationInput) => string;
  dismiss: (id: string) => void;
  clear: () => void;
  update: (id: string, patch: Partial<Notification>) => void;
}

export const useNotificationStore = create<NotificationState>((set) => ({
  notifications: [],
  push: (n) => {
    const id = uuidV4();
    const notification: Notification = {
      id,
      createdAt: Date.now(),
      durationMs: n.type === 'error' ? undefined : 5000,
      ...n
    };
    set((s) => ({ notifications: [...s.notifications, notification] }));
    return id;
  },
  dismiss: (id) =>
    set((s) => ({ notifications: s.notifications.filter((n) => n.id !== id) })),
  clear: () => set({ notifications: [] }),
  update: (id, patch) =>
    set((s) => ({
      notifications: s.notifications.map((n) => (n.id === id ? { ...n, ...patch } : n))
    }))
}));

export function notifySuccess(message: string, title?: string): string {
  return useNotificationStore.getState().push({ type: 'success', message, ...(title !== undefined ? { title } : {}) });
}

export function notifyError(messageOrError: string | Error, title?: string): string {
  const message = messageOrError instanceof Error ? messageOrError.message : messageOrError;
  return useNotificationStore.getState().push({ type: 'error', message, ...(title !== undefined ? { title } : {}) });
}

export function notifyInfo(message: string, title?: string): string {
  return useNotificationStore.getState().push({ type: 'info', message, ...(title !== undefined ? { title } : {}) });
}

export function notifyWarning(message: string, title?: string): string {
  return useNotificationStore.getState().push({ type: 'warning', message, ...(title !== undefined ? { title } : {}) });
}
